import React, { useState, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Star,
  StarHalf,
  Search,
  Calendar,
  MapPin,
  MessageSquare,
  Clock,
  Globe,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  Filter,
  X,
  SortAsc,
  SortDesc
} from 'lucide-react'
import { useGMBReviews, useGMBReviewStats, type ReviewFilters } from '@/hooks/useGMBReviews'
import { PremiumTable } from '@/components/enhanced/PremiumTable'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { normalizeStarRating, getStarFillStates, formatAverageRating } from '@/features/customers/reviews/utils/ratings'
import { formatRelativeDate } from '@/utils/displayFormat'

// Available franchise locations for filtering
const FRANCHISE_LOCATIONS = [
  { value: 'all', label: 'All Locations' },
  { value: 'hamilton', label: 'Hamilton' },
  { value: 'mississauga', label: 'Mississauga' },
  { value: 'kitchen-waterloo-guelph', label: 'Kitchen-Waterloo-Guelph' },
  { value: 'toronto', label: 'Toronto' },
  { value: 'london', label: 'London' },
  { value: 'niagara', label: 'Niagara' }
]

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First', icon: SortDesc },
  { value: 'oldest', label: 'Oldest First', icon: SortAsc },
  { value: 'highest_rating', label: 'Highest Rating', icon: Star },
  { value: 'lowest_rating', label: 'Lowest Rating', icon: Star }
]

export default function CustomerReviews() {
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedReview, setSelectedReview] = useState<any | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [filters, setFilters] = useState<ReviewFilters>({
    search: '',
    starRating: 'all',
    location: 'all',
    responseStatus: 'all',
    sortBy: 'newest'
  })
  const pageSize = 20

  const {
    data: reviewsData,
    isLoading: reviewsLoading,
    error: reviewsError
  } = useGMBReviews(currentPage, pageSize, filters)

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError
  } = useGMBReviewStats()

  // Helper functions for filter management
  const updateFilter = <K extends keyof ReviewFilters>(key: K, value: ReviewFilters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setCurrentPage(1) // Reset to first page when filters change
  }

  const clearAllFilters = () => {
    setFilters({
      search: '',
      starRating: 'all',
      location: 'all',
      responseStatus: 'all',
      sortBy: 'newest'
    })
    setCurrentPage(1)
  }

  // Count active filters for display
  const activeFiltersCount = useMemo(() => {
    let count = 0
    if (filters.search) count++
    if (filters.starRating !== 'all') count++
    if (filters.location !== 'all') count++
    if (filters.responseStatus !== 'all') count++
    if (filters.dateFrom) count++
    return count
  }, [filters])

  const getReviewRating = (review: any) => {
    const rawRating = review?.star_rating ?? review?.rating ?? review?.rating_value ?? review?.ratingValue ?? null
    const numericRating = normalizeStarRating(rawRating)
    const hasRating = rawRating !== null && rawRating !== undefined && numericRating > 0
    return { hasRating, numericRating }
  }

  if (reviewsError || statsError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center p-8">
          <div className="text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
            <h3 className="text-lg font-semibold">Error Loading Reviews</h3>
            <p className="text-muted-foreground">
              {reviewsError?.message || statsError?.message || 'Unable to load reviews data'}
            </p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Customer Reviews</h1>
          <p className="text-muted-foreground">
            View all Google My Business reviews from customers
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Data
          </Button>
          <Button variant="outline" size="sm">
            <TrendingUp className="w-4 h-4 mr-2" />
            View Analytics
          </Button>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reviews</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : stats?.total_reviews?.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground">
              <span className={`${(stats?.reviews_this_month || 0) >= 0 ? 'text-chart-5' : 'text-destructive'}`}>
                {(stats?.reviews_this_month || 0) >= 0 ? '+' : ''}{stats?.reviews_this_month || 0}
              </span> this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="text-2xl font-bold flex items-center gap-2">
                {statsLoading ? '...' : formatAverageRating(stats?.average_rating ?? null, stats?.total_reviews)}
              </div>
              {!statsLoading && (stats?.average_rating ?? null) !== null && (
                <div className="flex items-center gap-1" aria-label={`Average rating ${stats?.average_rating} out of 5`}>
                  {getStarFillStates(stats?.average_rating || 0).map((fill, idx) => {
                    if (fill === 'full') return <Star key={idx} className="w-4 h-4 fill-warning text-warning" />
                    if (fill === 'half') return <StarHalf key={idx} className="w-4 h-4 fill-warning text-warning" />
                    return <Star key={idx} className="w-4 h-4 text-muted-foreground" />
                  })}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              <span className={`${(stats?.rating_change || 0) >= 0 ? 'text-chart-5' : 'text-destructive'}`}>
                {(stats?.rating_change || 0) >= 0 ? '+' : ''}{stats?.rating_change || 0}
              </span> from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Response Rate</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : `${stats?.response_rate || 0}%`}
            </div>
            <p className="text-xs text-muted-foreground">
              <span className={`${(stats?.response_rate_change || 0) >= 0 ? 'text-chart-5' : 'text-destructive'}`}>
                {(stats?.response_rate_change || 0) >= 0 ? '+' : ''}{stats?.response_rate_change || 0}%
              </span> this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Responses</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : stats?.pending_responses || '0'}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.pending_responses && stats.pending_responses > 5 && (
                <span className="text-accent">Needs attention</span>
              )}
              {stats?.pending_responses && stats.pending_responses <= 5 && (
                <span className="text-chart-5">Under control</span>
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Reviews List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div>
              <CardTitle>Reviews</CardTitle>
              <CardDescription>
                All Google My Business reviews from customers
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search reviews..."
                  className="pl-10 w-64"
                  value={filters.search || ''}
                  onChange={(e) => updateFilter('search', e.target.value)}
                />
              </div>

              {/* Star Rating Filter */}
              <Select value={filters.starRating?.toString() || 'all'} onValueChange={(value) => updateFilter('starRating', value === 'all' ? 'all' : parseInt(value))}>
                <SelectTrigger className="w-40">
                  <Star className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Ratings" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="5">5 Stars</SelectItem>
                  <SelectItem value="4">4 Stars</SelectItem>
                  <SelectItem value="3">3 Stars</SelectItem>
                  <SelectItem value="2">2 Stars</SelectItem>
                  <SelectItem value="1">1 Star</SelectItem>
                </SelectContent>
              </Select>

              {/* Location Filter */}
              <Select value={filters.location || 'all'} onValueChange={(value) => updateFilter('location', value)}>
                <SelectTrigger className="w-48">
                  <MapPin className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Locations" />
                </SelectTrigger>
                <SelectContent>
                  {FRANCHISE_LOCATIONS.map(location => (
                    <SelectItem key={location.value} value={location.value}>
                      {location.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Response Status Filter */}
              <Select value={filters.responseStatus || 'all'} onValueChange={(value) => updateFilter('responseStatus', value as 'all' | 'responded' | 'needs_response')}>
                <SelectTrigger className="w-44">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Reviews" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reviews</SelectItem>
                  <SelectItem value="responded">Responded</SelectItem>
                  <SelectItem value="needs_response">Needs Response</SelectItem>
                </SelectContent>
              </Select>

              {/* Date Range Filter */}
              <Select
                value={
                  filters.dateFrom
                    ? `${filters.dateFrom}_${filters.dateTo || ''}`
                    : 'all'
                }
                onValueChange={(value) => {
                  if (value === 'all') {
                    updateFilter('dateFrom', undefined)
                    updateFilter('dateTo', undefined)
                  } else {
                    const today = new Date()
                    let fromDate: Date

                    switch (value) {
                      case 'week':
                        fromDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)
                        break
                      case 'month':
                        fromDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())
                        break
                      case '3months':
                        fromDate = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate())
                        break
                      case 'year':
                        fromDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())
                        break
                      default:
                        fromDate = today
                    }

                    updateFilter('dateFrom', fromDate.toISOString().split('T')[0])
                    updateFilter('dateTo', undefined)
                  }
                }}
              >
                <SelectTrigger className="w-40">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="week">Last Week</SelectItem>
                  <SelectItem value="month">Last Month</SelectItem>
                  <SelectItem value="3months">Last 3 Months</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort Options */}
              <Select value={filters.sortBy || 'newest'} onValueChange={(value) => updateFilter('sortBy', value as 'newest' | 'oldest' | 'highest_rating' | 'lowest_rating')}>
                <SelectTrigger className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(option => {
                    const IconComponent = option.icon
                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center">
                          <IconComponent className="w-4 h-4 mr-2" />
                          {option.label}
                        </div>
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>

              {/* Clear Filters Button */}
              {activeFiltersCount > 0 && (
                <Button variant="outline" size="sm" onClick={clearAllFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear ({activeFiltersCount})
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Active Filter Chips */}
          {activeFiltersCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {filters.search && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Search className="w-3 h-3" />
                  Search: "{filters.search}"
                  <button
                    onClick={() => updateFilter('search', '')}
                    className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}

              {filters.starRating !== 'all' && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Star className="w-3 h-3" />
                  {filters.starRating} Star{filters.starRating !== 1 ? 's' : ''}
                  <button
                    onClick={() => updateFilter('starRating', 'all')}
                    className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}

              {filters.location !== 'all' && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {FRANCHISE_LOCATIONS.find(loc => loc.value === filters.location)?.label}
                  <button
                    onClick={() => updateFilter('location', 'all')}
                    className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}

              {filters.responseStatus !== 'all' && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" />
                  {filters.responseStatus === 'responded' ? 'Has Response' : 'Needs Response'}
                  <button
                    onClick={() => updateFilter('responseStatus', 'all')}
                    className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}

              {filters.dateFrom && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Since: {new Date(filters.dateFrom).toLocaleDateString()}
                  <button
                    onClick={() => {
                      updateFilter('dateFrom', undefined)
                      updateFilter('dateTo', undefined)
                    }}
                    className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}

              {filters.sortBy !== 'newest' && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <Filter className="w-3 h-3" />
                  Sorted: {SORT_OPTIONS.find(opt => opt.value === filters.sortBy)?.label}
                </Badge>
              )}
            </div>
          )}

          {/* Reviews Table */}
          <PremiumTable
            data={reviewsData?.reviews || []}
            columns={[
              {
                header: 'Customer',
                cell: (review: any) => (
                  <div className="flex items-start gap-3">
                    <Avatar className="h-9 w-9 mt-0.5">
                      <AvatarFallback className="bg-primary/10 text-primary font-medium">
                        {review.reviewer_name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="space-y-1 min-w-0">
                      <div className="font-semibold text-foreground truncate" title={review.reviewer_name}>
                        {review.reviewer_name}
                      </div>
                      <div className="text-sm text-muted-foreground line-clamp-2">
                        {review.reviewer_comment}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="secondary" className="text-xs bg-chart-4/10 text-chart-4">
                          <Globe className="w-3 h-3 mr-1" />
                          GMB
                        </Badge>
                        {review.reply_comment && (
                          <Badge variant="default" className="text-xs bg-chart-5/10 text-chart-5">
                            <MessageSquare className="w-3 h-3 mr-1" />
                            Responded
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )
              },
              {
                header: 'Location',
                cell: (review: any) => (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm truncate">
                      {review.location_display_name || review.location_name || 'Fix My Brick'}
                      {review.address_city && ` (${review.address_city})`}
                    </span>
                  </div>
                )
              },
              {
                header: 'Rating',
                cell: (review: any) => (
                  <div className="flex items-center gap-2">
                    {(() => {
                      const rating = getReviewRating(review)
                      const starStates = getStarFillStates(rating.hasRating ? rating.numericRating : 0)
                      return (
                        <>
                          <div
                            className="flex space-x-1"
                            aria-label={
                              rating.hasRating
                                ? `Rating ${rating.numericRating.toFixed(1)} out of 5`
                                : 'Rating not available'
                            }
                          >
                            {starStates.map((fill, idx) => {
                              if (fill === 'full') return <Star key={idx} className="w-4 h-4 fill-warning text-warning" />
                              if (fill === 'half') return <StarHalf key={idx} className="w-4 h-4 fill-warning text-warning" />
                              return <Star key={idx} className="w-4 h-4 text-muted-foreground" />
                            })}
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {rating.hasRating ? rating.numericRating.toFixed(1) : 'N/A'}
                          </span>
                        </>
                      )
                    })()}
                  </div>
                )
              },
              {
                header: 'Date',
                cell: (review: any) => (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    {(() => {
                      const dateParts = formatRelativeDate(review.reviewer_create_time || review.created_at)
                      return (
                        <div className="text-sm leading-tight">
                          {dateParts.relative && <div className="font-medium">{dateParts.relative}</div>}
                          <div className="text-muted-foreground">{dateParts.absolute}</div>
                        </div>
                      )
                    })()}
                  </div>
                )
              }
            ]}
            isLoading={reviewsLoading}
            pagination={{
              currentPage: currentPage,
              totalPages: reviewsData?.total_pages || 1,
              onPageChange: setCurrentPage
            }}
            onRowClick={(review) => {
              setSelectedReview(review)
              setIsDialogOpen(true)
            }}
            emptyMessage={
              activeFiltersCount > 0
                ? 'No reviews match your current filters'
                : 'No reviews found'
            }
          />
        </CardContent>
      </Card>

      {/* Review Detail Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          {selectedReview && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary/10 text-primary font-medium">
                      {selectedReview.reviewer_name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="text-lg font-semibold">{selectedReview.reviewer_name || 'Unknown reviewer'}</div>
                    <DialogDescription className="flex items-center gap-2">
                      {selectedReview.location_display_name || selectedReview.location_name || 'Fix My Brick'}
                      {selectedReview.address_city && ` (${selectedReview.address_city})`}
                    </DialogDescription>
                  </div>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium">Rating:</div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const rating = getReviewRating(selectedReview)
                      const starStates = getStarFillStates(rating.hasRating ? rating.numericRating : 0)
                      return (
                        <>
                          <div className="font-semibold">{rating.hasRating ? rating.numericRating.toFixed(1) : 'N/A'}</div>
                          <div
                            className="flex items-center gap-1"
                            aria-label={
                              rating.hasRating
                                ? `Rating ${rating.numericRating.toFixed(1)} out of 5`
                                : 'Rating not available'
                            }
                          >
                            {starStates.map((fill, idx) => {
                              if (fill === 'full') return <Star key={idx} className="w-4 h-4 fill-warning text-warning" />
                              if (fill === 'half') return <StarHalf key={idx} className="w-4 h-4 fill-warning text-warning" />
                              return <Star key={idx} className="w-4 h-4 text-muted-foreground" />
                            })}
                          </div>
                        </>
                      )
                    })()}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Customer Comment</div>
                  <div className="rounded-md bg-muted/50 p-3 text-sm text-foreground whitespace-pre-wrap">
                    {selectedReview.reviewer_comment || 'No comment provided.'}
                  </div>
                </div>

                {selectedReview.reply_comment && (
                  <div className="space-y-2">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">Business Reply</div>
                    <div className="rounded-md bg-primary/5 p-3 text-sm text-foreground whitespace-pre-wrap">
                      {selectedReview.reply_comment}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {(() => {
                      const dateParts = formatRelativeDate(
                        selectedReview.reviewer_create_time || selectedReview.created_at,
                        { includeTime: true }
                      )
                      if (dateParts.absolute === '—') return <span>Unknown date</span>

                      return (
                        <span className="flex flex-col leading-tight">
                          {dateParts.relative && <span className="font-medium text-foreground">{dateParts.relative}</span>}
                          <span>{dateParts.absolute}</span>
                        </span>
                      )
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    <span>{selectedReview.responseStatus === 'responded' || selectedReview.reply_comment ? 'Responded' : 'Needs response'}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
